import { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Linking,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { ShieldCheck, Check } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { SPACE, RADIUS } from '@/constants/tokens';
import OnboardingProgress from '@/components/OnboardingProgress';
import Constants from 'expo-constants';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function TermsAcceptScreen() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();

  const [tosChecked, setTosChecked] = useState(false);
  const [liabilityChecked, setLiabilityChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canContinue = tosChecked && liabilityChecked;

  const handleContinue = () => {
    if (!canContinue || !user?.id) return;
    setSaving(true);

    // Navigate immediately based on current tier — don't block on Supabase
    const tier = (profile as any)?.onboarding_tier || 'competitor';
    if (profile?.onboarding_completed) {
      router.replace('/(tabs)/home');
    } else if (tier === 'casual') {
      router.replace('/(tabs)/home');
    } else {
      router.replace('/(onboarding)/baseline');
    }

    // Save ToS acceptance in background
    const uid = user.id;
    const now = new Date().toISOString();
    void Promise.resolve(supabase.from('profiles').update({ tos_accepted_at: now }).eq('id', uid));
    void Promise.resolve(supabase.from('tos_acceptances').insert({
      user_id: uid,
      tos_version: '1.0',
      privacy_version: '1.0',
      app_version: Constants.expoConfig?.version ?? '1.0.0',
    }));
    if (tier === 'casual') {
      void Promise.resolve(supabase.from('profiles').update({ onboarding_completed: true }).eq('id', uid));
    }
    refreshProfile?.().catch(() => {});
    setSaving(false);
  };

  const Checkbox = ({
    checked,
    onPress,
    children,
  }: {
    checked: boolean;
    onPress: () => void;
    children: React.ReactNode;
  }) => (
    <TouchableOpacity
      style={styles.checkboxRow}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View
        style={[
          styles.checkbox,
          {
            borderColor: checked ? c.accent : c.border,
            backgroundColor: checked ? c.accent : 'transparent',
          },
        ]}
      >
        {checked && <Check size={16} color={c.accentForeground} strokeWidth={3} />}
      </View>
      <View style={styles.checkboxContent}>{children}</View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={[styles.inner, { paddingTop: insets.top + 16 }]}>
          <OnboardingProgress step={5} />
          <ShieldCheck size={40} color={c.accent} />
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            TERMS &amp; LIABILITY
          </Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            Please review and accept before continuing
          </Text>

          <View style={styles.checkboxes}>
            <Checkbox checked={tosChecked} onPress={() => setTosChecked((v) => !v)}>
              <Text style={[styles.checkboxText, { color: c.text }]}>
                I agree to the{' '}
                <Text
                  style={[styles.link, { color: c.accent }]}
                  onPress={() => Linking.openURL('https://douevenlift.com/terms')}
                >
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text
                  style={[styles.link, { color: c.accent }]}
                  onPress={() => Linking.openURL('https://douevenlift.com/privacy')}
                >
                  Privacy Policy
                </Text>
                {/* Phase 2E: age requirement note */}
                <Text style={{ color: c.textTertiary }}>
                  {' '}· I confirm I am at least 13 years old (or have completed guardian consent).
                </Text>
              </Text>
            </Checkbox>

            <Checkbox checked={liabilityChecked} onPress={() => setLiabilityChecked((v) => !v)}>
              <Text style={[styles.checkboxText, { color: c.text }]}>
                I acknowledge the Liability Waiver:{' '}
                <Text style={{ color: c.textSecondary }}>
                  I understand that DUEL is a fitness tracking and competition app. I take full
                  responsibility for my own training decisions, safety, and physical health. DUEL is
                  not responsible for any injuries sustained during workouts.
                </Text>
              </Text>
            </Checkbox>
          </View>

          {error ? (
            <Text style={[styles.errorText, { color: c.error || '#ff4444' }]}>{error}</Text>
          ) : null}
        </View>
      </ScrollView>

      <View style={styles.bottom}>
        <Pressable
          style={[
            styles.primaryBtn,
            {
              backgroundColor: c.accent,
              opacity: canContinue && !saving ? 1 : 0.4,
            },
          ]}
          onPress={handleContinue}
          disabled={!canContinue || saving}
          android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
          hitSlop={{ top: 8, bottom: 8 }}
          accessibilityRole="button"
        >
          {saving ? (
            <ActivityIndicator size="small" color={c.accentForeground} />
          ) : (
            <Text
              style={[
                styles.primaryBtnText,
                { color: c.accentForeground, fontFamily: TYPO.heading.bold },
              ]}
            >
              CONTINUE
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  inner: {
    flex: 1,
    paddingHorizontal: SPACE['6'],
    paddingTop: 80,
    alignItems: 'center',
    gap: SPACE['5'],
  },
  title: {
    fontSize: 28,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 340,
    marginBottom: SPACE['4'],
  },
  checkboxes: {
    width: '100%',
    gap: SPACE['5'],
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACE['3'],
  },
  checkbox: {
    width: 26,
    height: 26,
    borderWidth: 2,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checkboxContent: {
    flex: 1,
  },
  checkboxText: {
    fontSize: 14,
    lineHeight: 22,
  },
  link: {
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
  errorText: {
    fontSize: 13,
    textAlign: 'center',
  },
  bottom: {
    paddingHorizontal: SPACE['6'],
    paddingBottom: 40,
    alignItems: 'center',
  },
  primaryBtn: {
    width: '100%',
    paddingVertical: 18,
    borderRadius: 0,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontSize: 18,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
});

